import React, { useEffect, useState } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import Text from '../text';
import Button from '../button';
import UsersTable from '../table/UsersTable';
import DefaultDialog from '../dialog/DefaultDialog';
import ApiService from 'utils/services/ApiService';
import ShareWithNewUser from './ShareWithNewUser';
import { usePopupMessage } from '../../hooks/SendPopUpMessage';
import { deleteUserData, getTestResults, saveTestResults } from '../../utils/services/idbService';
import { addEventLog } from 'utils/functions/eventLogFunctions';
import { useDispatch } from 'react-redux';
import { setAllSitesData } from 'utils/redux/reducers/allSitesLoadedReducer';

interface ManageUsersProps {
	isOwner?: boolean;
	siteId: number;
  isOnline: boolean
}

const ManageUsers: React.FC<ManageUsersProps> = ({ isOwner, siteId, isOnline }) => {
	const sendPopUpMessage = usePopupMessage();
	const [openDialog, setOpenDialog] = useState(false);
	const [showNewUser, setShowNewUser] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const loggedUser = JSON.parse(
		localStorage.getItem('user_information') || '{}'
	);
	const dispatch = useDispatch();

	const headers = [
		{ name: 'Owner', field: 'owner', colSpan: 'col-span-1' },
		{ name: 'User', field: 'user', colSpan: 'col-span-4' },
		{ name: 'Email', field: 'email', colSpan: 'col-span-4' },
		{ name: '', field: 'action', colSpan: 'col-span-3' },
	];

	const [users, setUsers] = useState<any[]>([]);
	const [selectedUser, setSelectedUser] = useState<any>();

	const addNewSiteOwner = async (
		newOwner: any,
		user: any,
		hideConfirmation?: boolean
	): Promise<boolean> => {
		const isNewToList = !users.some((u: any) => u.email === user.email);
		const previousUsers = users;

		if (isNewToList) {
			setUsers((prev) => [...prev, { ...user, siteOwnershipFlag: true }]);
		} else {
			setUsers((prev) =>
				prev.map((u: any) =>
					u.email === user.email ? { ...u, siteOwnershipFlag: true } : u
				)
			);
		}

		try {
			const result = await ApiService.addSiteOwner(newOwner, siteId, user);
			if (result.status !== 200)
				throw Error('Error to connect with the server.');
			if (hideConfirmation)
				sendPopUpMessage(
					'Success',
					user.firstName + ' is now a site owner!'
				);
			await deleteUserData(user?.email, siteId);
			return true;
		} catch (error) {
			console.log(error);
			setUsers(previousUsers);
			if (hideConfirmation)
				sendPopUpMessage(
					'Error',
					'Error adding this user as an owner. Please try again.'
				);
			return false;
		}
	};

	//event logging is handled in ApiService for this function.
	const removeASiteOwner = async (newOwner: any, user: any): Promise<boolean> => {
		const previousUsers = users;
		setUsers((prev) =>
			prev.map((u: any) =>
				u.email === user.email ? { ...u, siteOwnershipFlag: false } : u
			)
		);

		try {
			const result = await ApiService.removeSiteOwner(newOwner, siteId, user);
			if (result.status !== 200)
				throw Error('Error to connect with the server.');
			sendPopUpMessage(
				'Success',
				user.firstName + ' is no longer a site owner.'
			);
			return true;
		} catch (error) {
			console.log(error);
			setUsers(previousUsers);
			sendPopUpMessage(
				'Error',
				'Error removing this user as an owner. Please try again.'
			);
			return false;
		}
	};

	const handleOwner = async (
		user: any,
		index: number,
		checked: boolean
	): Promise<boolean> => {
		const newOwner = {
			siteId: siteId,
			ownerId: loggedUser.id,
			users: [user.id],
		};
		if (checked) return await addNewSiteOwner(newOwner, user, true);
		return await removeASiteOwner(newOwner, user);
	};

	const getSiteInformation = async () => {
		let siteInformation;
		try {
			const result = await ApiService.getASite(siteId);
			if (result.status !== 200)
				throw Error('Error connecting with the server.');
			siteInformation = result.data;
		} catch (error) {
			console.log(error);
		}
		return siteInformation;
	};

	const handleDeleteUser = (user: any) => {
		setOpenDialog(true);
		setSelectedUser(user);
	};

	const removeUserFromSite = (siteInformation: any) => {
		siteInformation.coOwnerVOs.forEach((coOwner: any, index: number) => {
			if (coOwner.email === selectedUser.email) {
				siteInformation.coOwnerVOs.splice(index, 1);
			}
		});
		siteInformation.userVOs.forEach((user: any, index: number) => {
			if (user.email === selectedUser.email) {
				siteInformation.userVOs.splice(index, 1);
			}
		});
		return siteInformation;
	};

	//throws error so a revert can be performed
	const updateSite = async (siteInformation: any, skipEventLog?: boolean) => {
		const result = await ApiService.editASite(siteInformation, siteId, skipEventLog);
		if (result.status !== 200)
			throw Error('Error connecting with the server.');
	};

	async function removeOwnSiteFromCache(siteId: number) {

		let allSitesCache = await getTestResults(
			`allSites`,
			localStorage.getItem('currentUser') ?? 'unknown'
		);
		let updatedAllSitesCache = allSitesCache.filter(
			(cachedSite: { id: number }) => cachedSite.id !== siteId
		);
		await saveTestResults(
			`allSites`,
			updatedAllSitesCache,
			localStorage.getItem('currentUser') ?? 'unknown'
		);
		dispatch(setAllSitesData(updatedAllSitesCache));

	}

	const handleDeleteUserConfirmation = async () => {
		setOpenDialog(false);
		const userToRemove = selectedUser;
		const previousUsers = users;

		//assume success and remove user, then revert if the API call fails
		setUsers((prev) => prev.filter((u: any) => u.email !== userToRemove.email));

		try {
			const siteInformation = await getSiteInformation();
			const updatedSiteInformation = removeUserFromSite(siteInformation);
			await updateSite(updatedSiteInformation, true);

			sendPopUpMessage(
				'Success',
				userToRemove.email + ' successfully removed from this site.'
			);
			addEventLog('Unshare Site Success', 'Site Management', siteId, undefined, `Email: ${userToRemove.email}`);

			if (userToRemove.email === loggedUser.email) {
				await removeOwnSiteFromCache(siteId);
			} else {
				await deleteUserData(userToRemove?.email, siteId);
			}
		} catch (error) {
			console.log(error);
			setUsers(previousUsers);
			sendPopUpMessage(
				'Error',
				'Error removing ' +
					userToRemove.email +
					' from this site. Please try again.'
			);
			addEventLog('Unshare Site Failed', 'Site Management', siteId, undefined, `Email: ${userToRemove.email}`);
		}
	};

	const handleShareWithNewUser = () => {
		setShowNewUser(true);
	};

	const handleSiteOwner = async (data: any, userEmail: string) => {
		let newOwner;
		let newUser;
		data.forEach((user: any) => {
			if (user.email === userEmail) {
				newOwner = {
					siteId: siteId,
					ownerId: loggedUser.id,
					users: [user.id],
				};
				newUser = user;
			}
		});
		await addNewSiteOwner(newOwner, newUser);
	};

	const getSiteUsers = async (addLoading?: boolean) => {
		try {
			if (addLoading) setIsLoading(true);
			const result = await ApiService.getSiteUsers(siteId);
			if (result.status !== 200)
				throw Error('Error connecting with the server.');
			setUsers(result.data);
		} catch (error) {
			console.log(error);
		}
		setIsLoading(false);
	};

	useEffect(() => {
		const fetchData = async () => {
			await getSiteUsers(true);
		};
		fetchData();
	}, []);

	return (
		<div id="manage-users">
        {!isOnline && (
          <div
              style={{
                width: '736px',
                border: '1px solid #FFA726',
                backgroundColor: '#FFF4E5',
                padding: '8px',
                display: 'block',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '10px',
                marginTop: '10px',
                marginRight: '20px',
              }}
            >
            <div style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/assets/icons/Wifi-orange.svg"
              alt="Offline"
              style={{ marginRight: '10px' }}
            />
            <Typography
              variant="body1"
              color="#66491F"
              className="!font-jost"
            >
              You're currently offline. Please reconnect to make
              changes.
            </Typography>
          </div>
        </div>
        )}
			<Text addStyle="!text-[#2C2C2C] !font-inter !text-[20px] !mt-[24px] !font-semibold !mb-[10px]">
				Manage Users
			</Text>
			<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px] !mt-[24px]">
				Add or remove user access to this site.
			</Text>
			<div className="mt-[32px]">
				{isLoading ? (
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: '200px',
						}}
					>
						<CircularProgress />
					</div>
				) : (
					<UsersTable
						headers={headers}
						users={users}
						handleOwner={handleOwner}
						handleDeleteUser={handleDeleteUser}
						isOwner={isOwner}
					/>
				)}
				{showNewUser && (
					<ShareWithNewUser
						siteId={siteId}
						setShowNewUser={setShowNewUser}
						handleSiteOwner={handleSiteOwner}
						users={users}
						setUsers={setUsers}
						sendPopUpMessage={sendPopUpMessage}
					/>
				)}
				{isOwner && (
					<div className="mt-[20px]">
						<Button
							onClick={handleShareWithNewUser}
							addStyle={`!h-[48px] !font-bold !text-[#0092C8] !text-[14px] !font-bold 
                    disabled:!text-[#9dabb4]`}
              disabled={!isOnline}
						>
							+ Share with New User
						</Button>
					</div>
				)}
			</div>
			<DefaultDialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				onConfirm={handleDeleteUserConfirmation}
				text={`Are you sure you want to unshare the site with ${
					selectedUser && selectedUser.email
				}?`}
			/>
		</div>
	);
};

export default ManageUsers;